import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

export type HubTheme = 'dark' | 'light'
const LS_KEY = 'th-theme'

type ThemeContextValue = {
  theme: HubTheme
  setTheme: (t: HubTheme) => void
  toggle: () => void
}

export const ThemeCtx = createContext<ThemeContextValue | null>(null)

function readInitial(): HubTheme {
  if (typeof window === 'undefined') return 'dark'
  try {
    const v = window.localStorage.getItem(LS_KEY)
    if (v === 'light' || v === 'dark') return v
  } catch {}
  return 'dark'
}

export function HubThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<HubTheme>(readInitial)

  useEffect(() => {
    try { window.localStorage.setItem(LS_KEY, theme) } catch {}
  }, [theme])

  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    setTheme,
    toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')),
  }), [theme])

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>
}

export function useHubTheme(): ThemeContextValue {
  const v = useContext(ThemeCtx)
  if (!v) throw new Error('useHubTheme must be used inside <HubThemeProvider>')
  return v
}
